import { Turnstile } from '@marsidev/react-turnstile';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { ValidationError } from '#api/errors.ts';
import { Spinner } from '#assets/Spinner.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { AuthLayout } from '#components/patterns/layouts/auth/AuthLayout.tsx';
import { CenteredLayout } from '#components/patterns/layouts/centered/CenteredLayout.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import type { InvalidMagicLinkType } from '../../types.ts';

// Testing keys: https://developers.cloudflare.com/turnstile/reference/testing/
const turnstileKey = import.meta.env.VITE_TURNSTILE_KEY;

type Props = {
  magicLinkToken: string;
  email?: string;
  onSuccess: () => void;
  onInvalidLink: (type: InvalidMagicLinkType) => void;
};

export function MagicLinkVerify({ magicLinkToken, email, onSuccess, onInvalidLink }: Props) {
  // Is the magic-link being verified by the API?
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  // If turnstile fails, show a manual button for the user to click
  const [showLoginBtn, setShowLoginBtn] = useState<boolean>(false);

  const { verifyMagicLink } = useAuth();

  const magicLinkVerificationMutation = useMutation({
    mutationFn: verifyMagicLink,
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      if (err instanceof ValidationError) {
        if (err.errors.some((error) => error.code === 'ERR_INVALID_MAGIC_LINK_TOKEN')) {
          return onInvalidLink('expired');
        }
        if (err.errors.some((error) => error.code === 'ERR_INVALID_VALUE')) {
          return onInvalidLink('invalid');
        }
      }
      onInvalidLink('failed');
    },
  });

  function submitToken() {
    magicLinkVerificationMutation.mutate(magicLinkToken);
    setIsVerifying(true);
  }

  async function checkTurnstile(token: string) {
    try {
      const turnstileResult = await fetch(`${location.origin}/ml-turnstile`, {
        method: 'POST',
        credentials: 'omit',
        body: JSON.stringify({
          'cf-turnstile-response': token,
        }),
      });
      const { success } = (await turnstileResult.json()) as { success: boolean };
      if (success) {
        submitToken();
      } else {
        setShowLoginBtn(true);
      }
    } catch (e) {
      console.error(e);
      // If there's any kind of problem, just show the login button
      setShowLoginBtn(true);
    }
  }

  return (
    <AuthLayout headerText={<span className="truncate">{email}</span>}>
      <CenteredLayout>
        {isVerifying || !showLoginBtn ?
          <Spinner size={48} />
        : <Button onClick={submitToken}>Continue login</Button>}
        <Turnstile
          siteKey={turnstileKey}
          options={{
            // As long as the widget is set as invisible in CF, it will never show up
            appearance: 'interaction-only',
            // Don't need to retry, we show a login button on failure
            retry: 'never',
            // We're not submitting a form, don't need a hidden input
            responseField: false,
          }}
          onSuccess={checkTurnstile}
          onError={() => {
            setShowLoginBtn(true);
          }}
          onUnsupported={() => {
            setShowLoginBtn(true);
          }}
          scriptOptions={{
            onError() {
              setShowLoginBtn(true);
            },
          }}
        />
      </CenteredLayout>
    </AuthLayout>
  );
}
